/**
 * Copy of Operations › PO Fulfilment (`components/Operations/PoFulfilment*`),
 * from VentaHub's `pages/POFillRate.tsx` and
 * `components/ops/AmazonPoBridgeDetailPanel.tsx`. The status glossary is
 * VentaHub's, so a PO reads the same on both systems while they run side by side.
 */
export const poFulfilment = {
  title: 'PO fulfilment',
  views: {
    label: 'Which purchase orders',
    amazon: 'Amazon vendor POs',
    retailer: 'Retailer POs',
  },
  subtitle:
    'Amazon Vendor purchase orders: how much was confirmed and delivered, and getting each one to the warehouse.',
  filters: 'PO filters',
  marketplace: 'Marketplace',
  pickMarketplace: 'Pick a marketplace',
  dates: 'PO date',
  status: 'Status',
  allStatuses: 'All statuses',
  prompt: {
    title: 'Pick a marketplace',
    hint: 'The page shows the Amazon purchase orders placed with your vendor stores on it.',
  },
  summary: {
    title: 'Summary by store',
    window: 'Purchase orders dated {{from}} to {{to}}',
    store: 'Store',
    pos: 'POs',
    skus: 'SKUs',
    ordered: 'Ordered',
    confirmed: 'Confirmed',
    delivered: 'Delivered',
    confirmRate: 'Confirmed rate',
    deliveryRate: 'Delivered rate',
    value: 'Ordered value',
    allStores: 'All stores',
    rateHint: '{{rate}} of ordered units',
    empty: 'No purchase orders for this marketplace in the selected dates.',
    export: 'Download report',
  },
  list: {
    card_one: '{{count}} purchase order',
    card_other: '{{count}} purchase orders',
    po: 'PO number',
    store: 'Store',
    date: 'Purchase date',
    skus: 'SKUs',
    ordered: 'Ordered',
    confirmed: 'Confirmed',
    delivered: 'Delivered',
    amount: 'Amount',
    status: 'Status',
    acknowledged: 'Amazon acknowledged',
    open: 'Open PO {{po}}',
    close: 'Close PO {{po}}',
    emptyTitle: 'No purchase orders',
    emptyHint: 'None were placed with this marketplace’s vendor stores in the selected dates.',
    filteredTitle: 'No purchase orders with this status',
    filteredHint: 'Pick another status, or all statuses.',
  },
  statuses: {
    PENDING: 'Pending',
    CLOSED: 'Closed',
    SKIPPED_NO_RETAILER: 'No retailer',
    SKIPPED_AMBIGUOUS_RETAILER: 'Several retailers',
    SKIPPED_NO_WAREHOUSE: 'No warehouse',
    CREATED_PENDING_EMAIL: 'Created, not sent',
    PARTIAL: 'Partial',
    BRIDGED_NO_EMAIL: 'Done, not mailed',
    BRIDGED: 'Done',
  },
  // VentaHub's BRIDGE_STATUS_DESCRIPTIONS, in lifecycle order.
  glossary: {
    label: 'What the statuses mean',
    PENDING: 'Not attempted yet.',
    CLOSED: 'Amazon closed the PO before anything was done with it.',
    SKIPPED_NO_RETAILER: 'The store has no retailer linked, so nothing can start.',
    SKIPPED_AMBIGUOUS_RETAILER:
      'The store has two or more retailers linked: which one to use is ambiguous.',
    SKIPPED_NO_WAREHOUSE: 'No warehouse priority is configured for the store.',
    CREATED_PENDING_EMAIL: 'PO created and stock blocked, not yet sent to the warehouse.',
    PARTIAL: 'Some units are short, or a warehouse’s leg failed to be created or blocked.',
    BRIDGED_NO_EMAIL: 'Fully covered and blocked, but a warehouse has no mail list to send to.',
    BRIDGED: 'Done: PO created, stock blocked and the warehouses told.',
  },
  ack: {
    PENDING: 'Pending',
    SUCCESS: 'Confirmed',
    FAILURE: 'Failed',
    failed: 'Amazon rejected the acknowledgement.',
  },
  detail: {
    label: 'Amazon PO {{po}}',
    loading: 'Loading PO {{po}}…',
    retailer: 'Retailer',
    priority: 'Warehouse priority',
    legs: 'Existing legs',
    amazonStatus: 'Amazon PO status',
    acknowledged: 'Amazon acknowledged',
    covered: 'Units blocked',
    uncoveredChip: '{{count}} uncovered',
    note: 'Note',
    none: '—',
    coverage: 'Coverage by warehouse',
    units: 'Units',
    worth: 'Worth',
    uncoveredRow: 'Uncovered',
    lines: 'Lines',
    sku: 'SKU code',
    titleCol: 'Title',
    asin: 'ASIN',
    ordered: 'Ordered',
    confirmed: 'Confirmed',
    delivered: 'Delivered',
    uncovered: 'Uncovered',
    uncoveredHint:
      'Units no candidate warehouse has in free stock: a stock gap, not the amount left to confirm.',
    price: 'Price',
    noLines: 'This purchase order has no lines.',
    unlisted: 'Not listed',
    closed: 'Amazon has closed this PO: no further actions are available.',
    done: 'Created, acknowledged and sent to the warehouses.',
    doneShort:
      'Created, acknowledged and sent, but only {{covered}} of {{ordered}} units are covered: the rest has no stock at any candidate warehouse.',
    stillOpen: 'Amazon still has this PO open, so the acknowledgement can be sent again.',
    writesOff:
      'PO fulfilment actions are switched off while VentaHub runs them: this page shows what Create PO would do, and changes nothing.',
  },
  actions: {
    create: 'Create PO',
    topUp: 'Top up PO',
    acknowledge: 'Submit acknowledgement',
    reacknowledge: 'Re-submit acknowledgement',
    retryAcknowledge: 'Retry acknowledgement',
    send: 'Send to warehouse',
    creating: 'Creating…',
    acknowledging: 'Submitting…',
    sending: 'Sending…',
    confirm: 'Confirm',
    cancel: 'Cancel',
    createTitle: 'Create PO {{po}}?',
    topUpTitle: 'Top up PO {{po}}?',
    createBody: 'This allocates and blocks:',
    topUpBody: 'This adds to the existing legs and blocks:',
    nothingToAllocate: 'No stock is available to allocate for this PO right now.',
    acknowledgeTitle: 'Submit the acknowledgement for {{po}}?',
    acknowledgeBody: 'Amazon is told the units blocked for this PO now, as accepted.',
    sendTitle: 'Send {{po}} to the warehouses?',
    sendBody: 'The packing team is mailed for:',
    created: 'PO created and stock blocked.',
    toppedUp: 'PO topped up and stock blocked.',
    acknowledged: 'Acknowledgement sent to Amazon.',
    sent: 'Warehouses notified.',
    failed: 'The action failed.',
  },
} as const
